import { ProfileItem } from "PFApp/components/profile_item";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Select } from "PFComponents/select";
import { getActiveActivityProfiles } from "PFCore/helpers/get_active_activity_profiles";
import { getProfileName, isProfileActive } from "PFCore/helpers/profile";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useOwnerCreate, useOwnerUpdate } from "PFCore/hooks/queries/owners";
import { Activity, ActivityProfileType, Profile } from "PFTypes";
import { Option } from "PFTypes/options";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOwnerRemove } from "../hooks/use_owner_remove";
import { UseAddActivityProfilesReturn } from "./activity_profiles.types";
import { AddActivityProfilesModalContent } from "./add_activity_profiles_modal_content";

type UseAddOwnersModalProps = {
  handleClose: VoidFunction;
  activity: Activity;
};

export const useAddOwnersModal = ({
  handleClose,
  activity
}: UseAddOwnersModalProps): UseAddActivityProfilesReturn => {
  const { owners } = activity;
  const { getFirstError } = useResponseErrors();
  const activityOwners = getActiveActivityProfiles(activity.owners);
  const currentPrincipalOwner = owners.find(({ principal }) => principal)?.profile || null;

  const [newProfiles, setNewProfiles] = useState<Profile[]>([]);
  const [principalOwner, setPrincipalOwner] = useState<Profile | null>(currentPrincipalOwner);
  const { t } = useTranslation("activities");
  const { t: tTranslation } = useTranslation();
  const { showGrowl } = useGrowlStoreActions();

  const { mutateAsync: createOwner } = useOwnerCreate();
  const { mutate: updateOwner } = useOwnerUpdate(activity.id);

  const { handleRemove, getRemoveModalContent } = useOwnerRemove(activity, handleClose);

  const principalOwnerOptions = useMemo(
    () =>
      owners
        .filter(({ profile }) => isProfileActive(profile))
        .map(({ profile }) => profile)
        .concat(newProfiles),
    [owners, newProfiles]
  );

  useEffect(() => {
    if (principalOwner && !principalOwnerOptions.find(({ id }) => principalOwner.id === id)) {
      setPrincipalOwner(null);
    }
  }, [principalOwnerOptions, principalOwner]);

  const handleReset = () => {
    setNewProfiles([]);
    setPrincipalOwner(currentPrincipalOwner);
  };

  const handleSubmit = () => {
    const removedPrincipalOwner = !principalOwner && owners.find((owner) => owner.principal);
    const existingPrincipalOwner =
      principalOwner && owners.find((owner) => owner.profile.id === principalOwner.id && !owner.principal);

    const principalOwnerToUpdate = removedPrincipalOwner || existingPrincipalOwner;

    if (principalOwnerToUpdate) {
      updateOwner(
        {
          id: principalOwnerToUpdate.id,
          principal: !!principalOwner
        },
        {
          onSuccess: () => {
            showGrowl({
              message: t("show.parts.principalOwnerUpdated"),
              kind: "success"
            });
          }
        }
      );
    }
    newProfiles.forEach((profile) =>
      createOwner({
        profileId: profile.id,
        activityId: activity.id,
        ...(principalOwner?.id === profile.id ? { principal: true } : {})
      })
        .then(() =>
          showGrowl({
            message: t("show.parts.profileIsCoOwner", {
              profileName: getProfileName(profile),
              activityName: activity.name
            }),
            kind: "success"
          })
        )
        .catch((error) => {
          const errorMessage = getFirstError(error);
          showGrowl({
            message: errorMessage || t("show.parts.activityNotShared", { activityName: activity.name }),
            kind: "error"
          });
        })
    );
  };

  const getModalContent = () => (
    <AddActivityProfilesModalContent
      activity={activity}
      profiles={activityOwners}
      onRemove={handleRemove}
      description={t("show.parts.ownersModalDescription")}
      newProfiles={newProfiles}
      onNewProfilesChange={setNewProfiles}
      getRemoveModalContent={getRemoveModalContent}
      activityProfileType={ActivityProfileType.Owner}
      selectQaId="ManageParticipants.OwnersSelect"
    >
      <Select<Profile>
        key={JSON.stringify(principalOwnerOptions.map((id) => id))}
        value={principalOwner?.id}
        label={t("show.parts.ownersSelectLabel")}
        placeholder={tTranslation("search")}
        renderDisplayValue={() => (principalOwner ? <ProfileItem profile={principalOwner} /> : null)}
        onChange={(option: Option<Profile>) => setPrincipalOwner(option.original)}
        fetchOptions={({ searchTerm = "" }) =>
          Promise.resolve({
            entries: principalOwnerOptions
              .filter((item) => getProfileName(item).toLowerCase().includes(searchTerm.toLowerCase()))
              .map((item) => ({
                id: item.id,
                displayElement: <ProfileItem profile={item} />,
                value: String(item.id),
                original: item
              })),
            meta: { total: principalOwnerOptions.length }
          })
        }
        onClear={principalOwner ? () => setPrincipalOwner(null) : undefined}
      />
    </AddActivityProfilesModalContent>
  );

  return {
    getModalContent,
    handleSubmit,
    handleReset,
    enableSubmit: principalOwner !== currentPrincipalOwner || newProfiles.length > 0
  };
};
